import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { Bell, BellRing, Calendar, Clock, X, Check, Volume2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AlarmNotifications = () => {
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const loadAlarms = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/calendar/upcoming-alarms`, {
        headers: getAuthHeaders()
      });
      setAlarms(res.data.alarms || []);
      
      // Check for overdue alarms and show toast
      const overdueAlarms = (res.data.alarms || []).filter(a => a.is_overdue && !a.alarm_dismissed);
      if (overdueAlarms.length > 0) {
        overdueAlarms.forEach(alarm => {
          toast.warning(`📢 Hatırlatıcı: ${alarm.title}`, {
            description: alarm.description || `${alarm.date} ${alarm.time || ''}`,
            duration: 10000,
          });
        });
      }
    } catch (error) {
      console.error('Alarm yüklenirken hata:', error);
    }
  }, []);

  useEffect(() => {
    loadAlarms();
    // Refresh alarms every 5 minutes
    const interval = setInterval(loadAlarms, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadAlarms]);

  const dismissAlarm = async (noteId) => {
    try {
      await axios.post(`${API}/calendar/dismiss-alarm/${noteId}`, {}, {
        headers: getAuthHeaders()
      });
      setAlarms(prev => prev.filter(a => a.id !== noteId));
      toast.success('Alarm kapatıldı');
    } catch (error) {
      toast.error('Alarm kapatılamadı');
    }
  };

  const formatAlarmTime = (alarmTime) => {
    if (!alarmTime) return '-';
    const date = new Date(alarmTime);
    const now = new Date();
    const diffMs = date - now;
    const diffMins = Math.round(diffMs / 60000);
    
    if (diffMins < 0) {
      return `${Math.abs(diffMins)} dk önce`;
    } else if (diffMins < 60) {
      return `${diffMins} dk sonra`;
    } else if (diffMins < 1440) {
      return `${Math.round(diffMins / 60)} saat sonra`;
    } else {
      return date.toLocaleString('tr-TR', { 
        month: 'short', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
  };

  const getColorClass = (color) => {
    const colors = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      red: 'bg-red-500',
      yellow: 'bg-yellow-500',
      purple: 'bg-purple-500',
      orange: 'bg-orange-500',
      pink: 'bg-pink-500',
      cyan: 'bg-cyan-500',
    };
    return colors[color] || 'bg-blue-500';
  };

  const activeAlarms = alarms.filter(a => !a.alarm_dismissed);
  const hasOverdue = activeAlarms.some(a => a.is_overdue);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="relative"
          data-testid="alarm-bell"
        >
          {hasOverdue ? (
            <BellRing className="h-5 w-5 text-orange-500 animate-pulse" />
          ) : (
            <Bell className="h-5 w-5 text-gray-600" />
          )}
          {activeAlarms.length > 0 && (
            <Badge 
              className={`absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs ${
                hasOverdue ? 'bg-orange-500' : 'bg-blue-500'
              }`}
            >
              {activeAlarms.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-3 border-b">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Hatırlatıcılar
            </h3>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={loadAlarms}
              className="h-7 px-2 text-xs"
            >
              Yenile
            </Button>
          </div>
        </div>
        
        <ScrollArea className="max-h-80">
          {activeAlarms.length > 0 ? (
            <div className="p-2 space-y-2">
              {activeAlarms.map((alarm) => (
                <div 
                  key={alarm.id}
                  className={`p-3 rounded-lg border ${
                    alarm.is_overdue 
                      ? 'bg-orange-50 border-orange-200' 
                      : 'bg-gray-50 border-gray-200'
                  }`}
                  data-testid={`alarm-item-${alarm.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${getColorClass(alarm.color)}`}></div>
                        <p className="font-medium text-sm text-gray-800 truncate">
                          {alarm.title}
                        </p>
                      </div>
                      {alarm.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {alarm.description}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {alarm.date}
                        </span>
                        {alarm.time && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {alarm.time}
                          </span>
                        )}
                      </div>
                      <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${
                        alarm.is_overdue ? 'text-orange-600' : 'text-blue-600'
                      }`}>
                        <Volume2 className="h-3 w-3" />
                        {formatAlarmTime(alarm.alarm_time)}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-gray-400 hover:text-gray-600"
                      onClick={() => dismissAlarm(alarm.id)}
                      title="Alarmı kapat"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <Bell className="h-10 w-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Yaklaşan hatırlatıcı yok</p>
              <p className="text-xs mt-1">Takvimden alarm ekleyebilirsiniz</p>
            </div>
          )}
        </ScrollArea>
        
        {activeAlarms.length > 0 && (
          <div className="p-2 border-t">
            <a 
              href="/work/calendar" 
              className="block text-center text-sm text-blue-600 hover:text-blue-700 py-1"
            >
              Takvime Git →
            </a>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default AlarmNotifications;
